import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FoldingList } from '@/components/common';
import { useSelfContext } from '@/context';
import { fetchActivityLogs } from '@/services/activity.service';

const PAGE_SIZE = 200;

export interface ActivityReportMobileProps {
  fromDate: string;
  toDate: string;
  actionTypes: string[];
}

export function ActivityReportMobile({
  fromDate,
  toDate,
  actionTypes,
}: ActivityReportMobileProps) {

  const { findVariantById, loading: selfLoading } = useSelfContext();
  const [t] = useTranslation(['common', 'report']);

  const logsQuery = useInfiniteQuery({
    retry: 0,
    enabled: !selfLoading,
    queryKey: ['/rest/users/activity-logs', 'infinite', fromDate, toDate, actionTypes],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchActivityLogs({
      actionTypes,
      page: pageParam,
      limit: PAGE_SIZE,
      fromDate,
      toDate,
      t,
      findVariantById,
    }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      if (loadedCount >= lastPage.total) {
        return undefined;
      }
      return allPages.length;
    },
  });

  const items = useMemo(
    () => logsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [logsQuery.data?.pages]
  );

  const mapped = items.map(item => ({
    title: item.name,
    description: item.creditType
      ? `${item.date} ${item.time} · ${item.creditType}`
      : `${item.date} ${item.time}`,
    attrs: item?.details as any,
  }));

  return (
    <FoldingList
      items={mapped}
      hasMore={logsQuery.hasNextPage ?? false}
      onEndReached={() => {
        if (logsQuery.hasNextPage && !logsQuery.isFetchingNextPage) {
          void logsQuery.fetchNextPage();
        }
      }}
    />
  );
}

export default ActivityReportMobile;
